import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Extracted components
import { 
  UsersManagement, 
  ReservationsTab, 
  ProductRequestsTab, 
  SettingsTab,
  PerformanceDashboard,
  SalesManagement,
  DataManagement
} from '../components/admin';

import AuditLogPanel from '../components/admin/AuditLogPanel';

import BlogManagement from '../components/admin/BlogManagement';
import GalleryManagement from '../components/admin/GalleryManagement';
import CommunityManagement from '../components/admin/CommunityManagement';
import AgentActivityDashboard from '../components/admin/AgentActivityDashboard';
import MarketingTab from '../components/admin/MarketingTab';
import AppearanceTab from '../components/admin/AppearanceTab';
import SMSManagement from '../components/admin/SMSManagement';
import MedicielAutoTab from '../components/admin/MedicielAutoTab';
import MedicielRealtimeTab from '../components/admin/MedicielRealtimeTab';
import InventoryTab from '../components/admin/InventoryTournant/InventoryTab';
import ReceptionTab from '../components/admin/Reception/ReceptionTab';
import AgentPerformanceTab from '../components/admin/AgentPerformance/AgentPerformanceTab';
import ScoringTab from '../components/admin/Scoring/ScoringTab';
import SessionConfigTab from '../components/admin/System/SessionConfigTab';
import B2BTab from '../components/admin/B2B/B2BTab';
import NotificationPreferencesTab from '../components/admin/NotificationPreferencesTab';
import AcademyTab from '../components/admin/Academy/AcademyTab';
import JobPostingsTab from '../components/admin/JobPostings/JobPostingsTab';
import StatsPage from './StatsPage';

import { 
  ArrowLeft, 
  Plus, 
  Upload, 
  Package, 
  Pencil, 
  Trash2,
  Loader2,
  FileSpreadsheet,
  Search,
  Image,
  Link,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart3,
  Filter,
  Bell,
  BellOff,
  Settings,
  Database,
  RefreshCw,
  FileText,
  Clock,
  ShoppingCart,
  Phone,
  HelpCircle,
  Mail,
  MessageSquare,
  Star,
  Users,
  UserPlus,
  Key,
  Shield,
  TrendingUp,
  Award,
  Activity,
  Megaphone,
  Palette,
  ClipboardList,
  Lock,
  Truck,
  Building2,
  GraduationCap,
  Briefcase
} from 'lucide-react';
import axios from 'axios';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category definitions
const CATEGORIES = {
  cosmetique: {
    label_fr: 'Cosmétiques',
    label_en: 'Cosmetics',
    color: 'bg-pink-100 text-pink-800',
    subcategories: [
      { value: 'nettoyant', label_fr: 'Nettoyant', label_en: 'Cleanser' },
      { value: 'hydratant', label_fr: 'Hydratant', label_en: 'Moisturizer' },
      { value: 'solaire', label_fr: 'Solaire / SPF', label_en: 'Sunscreen' },
      { value: 'anti-taches', label_fr: 'Anti-taches / Éclaircissant', label_en: 'Anti-spots' },
      { value: 'anti-rides', label_fr: 'Anti-rides / Anti-âge', label_en: 'Anti-wrinkle' },
      { value: 'acne', label_fr: 'Anti-acné / Imperfections', label_en: 'Anti-acne' },
      { value: 'sensible', label_fr: 'Peau sensible / Apaisant', label_en: 'Sensitive skin' },
      { value: 'corps', label_fr: 'Soins corps', label_en: 'Body care' },
      { value: 'bebe', label_fr: 'Bébé / Enfant', label_en: 'Baby / Child' },
      { value: 'cheveux', label_fr: 'Cheveux', label_en: 'Hair' }
    ]
  },
  complement: {
    label_fr: 'Compléments alimentaires',
    label_en: 'Dietary Supplements',
    color: 'bg-green-100 text-green-800',
    subcategories: [
      { value: 'vitamines', label_fr: 'Vitamines', label_en: 'Vitamins' },
      { value: 'mineraux', label_fr: 'Minéraux', label_en: 'Minerals' },
      { value: 'plantes', label_fr: 'Plantes', label_en: 'Herbal' },
      { value: 'nutrition', label_fr: 'Nutrition', label_en: 'Nutrition' },
      { value: 'bien-etre', label_fr: 'Bien-être', label_en: 'Wellness' }
    ]
  },
  spa: {
    label_fr: 'Spa / Soins',
    label_en: 'Spa / Care',
    color: 'bg-purple-100 text-purple-800',
    subcategories: [
      { value: 'gommage', label_fr: 'Gommage / Exfoliant', label_en: 'Scrub' },
      { value: 'masque', label_fr: 'Masque', label_en: 'Mask' },
      { value: 'accessoires', label_fr: 'Accessoires (gants, éponges)', label_en: 'Accessories' },
      { value: 'huiles', label_fr: 'Huiles', label_en: 'Oils' }
    ]
  },
  consommable: {
    label_fr: 'Consommables',
    label_en: 'Consumables',
    color: 'bg-gray-100 text-gray-800',
    subcategories: [
      { value: 'coton', label_fr: 'Coton / Compresse', label_en: 'Cotton / Compress' },
      { value: 'hygiene', label_fr: 'Hygiène', label_en: 'Hygiene' },
      { value: 'divers', label_fr: 'Divers', label_en: 'Miscellaneous' }
    ]
  },
  divers: {
    label_fr: 'Divers',
    label_en: 'Miscellaneous',
    color: 'bg-yellow-100 text-yellow-800',
    subcategories: []
  }
};

// Pharmacies de Garde Upload Component
const GardeUploadSection = () => {
  const [uploading, setUploading] = useState(false);
  const [gardeMonth, setGardeMonth] = useState('');
  const [currentGarde, setCurrentGarde] = useState(null);
  const fileInputRef = React.useRef(null);
  const { token } = useAuth();

  React.useEffect(() => {
    fetchCurrentGarde();
  }, []);

  const fetchCurrentGarde = async () => {
    try {
      const response = await axios.get(`${API_URL}/pharmacy/garde`);
      setCurrentGarde(response.data);
    } catch (error) {
      console.error('Error fetching garde data:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !gardeMonth) {
      toast.error('Veuillez sélectionner un mois et un fichier PDF');
      return;
    }

    if (!file.name.endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', gardeMonth);

    try {
      const response = await axios.post(`${API_URL}/pharmacy/garde/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${response.data.total_pharmacies} pharmacies de garde importées`);
      fetchCurrentGarde();
      setGardeMonth('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const months = [
    'Janvier 2026', 'Février 2026', 'Mars 2026', 'Avril 2026', 'Mai 2026', 'Juin 2026',
    'Juillet 2026', 'Août 2026', 'Septembre 2026', 'Octobre 2026', 'Novembre 2026', 'Décembre 2026'
  ];

  return (
    <div className="p-4 bg-gradient-to-r from-[#1E3A5F]/10 to-[#1E3A5F]/5 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#1E3A5F]/20 rounded-lg flex items-center justify-center">
          <Clock className="w-5 h-5 text-[#1E3A5F]" />
        </div>
        <div>
          <p className="font-medium text-[#5D4E37]">Pharmacies de Garde</p>
          <p className="text-sm text-[#8B7355]">
            {currentGarde?.month ? `Actuel: ${currentGarde.month} (${currentGarde.total_pharmacies || 0} pharmacies)` : 'Aucune donnée'}
          </p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3">
        <select
          value={gardeMonth}
          onChange={(e) => setGardeMonth(e.target.value)}
          className="flex-1 min-w-[150px] p-2 border border-[#E8DDD4] rounded-lg text-sm"
        >
          <option value="">Sélectionner le mois</option>
          {months.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        
        <input
          type="file"
          accept=".pdf"
          onChange={handleUpload}
          ref={fileInputRef}
          className="hidden"
          id="garde-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !gardeMonth}
          className="bg-[#1E3A5F] hover:bg-[#162D4A]"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Import...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Uploader PDF
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const AdminPage = ({ onBack, onNavigateToImport, initialMainTab }) => {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false since we don't load products by default
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // Main page tabs
  const [mainTab, setMainTab] = useState(initialMainTab || 'products');
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Catégorie admin (niveau 1) : SkinCare / Health / Rayons / GAS / Système / Client / RH
  // Mapping de chaque tab vers sa catégorie pour pré-positionner le menu
  const TAB_CATEGORY = {
    // Admin SkinCare — conseil & catalogue + ventes
    products: 'skincare',
    stats: 'skincare',
    sales: 'skincare',
    // Admin Rayons — inventaire & stock catalogue
    inventaire: 'rayons',
    'mediciel-auto': 'rayons',
    'mediciel-realtime': 'rayons',
    'import-mediciel': 'rayons',
    // Admin GAS — réception
    reception: 'gas',
    // Admin Système — contenu & paramètres transverses
    blog: 'systeme',
    gallery: 'systeme',
    community: 'systeme',
    marketing: 'systeme',
    appearance: 'systeme',
    settings: 'systeme',
    data: 'systeme',
    notifications: 'systeme',
    // Admin Client — relation client
    reservations: 'client',
    'product-requests': 'client',
    sms: 'client',
    // Admin RH — équipes & performance
    users: 'rh',
    performance: 'rh',
    'agent-performance': 'rh',
    'scoring': 'rh',
    'agent-activity': 'rh',
    'recrutement': 'rh',
    // Admin Academy — formations
    academy: 'academy',
  };
  const [adminCategory, setAdminCategory] = useState(
    TAB_CATEGORY[initialMainTab] || 'skincare'
  );

  // Premier onglet de chaque catégorie (utilisé pour l'auto-switch)
  const CATEGORY_DEFAULT_TAB = {
    skincare: 'products',
    health: 'products', // pas de tab dédié health pour l'instant — reste sur products
    rayons: 'inventaire',
    gas: 'reception',
    client: 'reservations',
    rh: 'users',
    systeme: 'settings',
  };

  // Quand on change de catégorie, basculer mainTab sur le 1er onglet
  // de cette catégorie si l'onglet courant n'y appartient plus.
  useEffect(() => {
    if (TAB_CATEGORY[mainTab] !== adminCategory) {
      const next = CATEGORY_DEFAULT_TAB[adminCategory];
      if (next && next !== mainTab) {
        setMainTab(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminCategory]);

  // Apply initialMainTab when it changes (e.g. arriving from ModuleSelector)
  useEffect(() => {
    if (initialMainTab) {
      setMainTab(initialMainTab);
      setAdminCategory(TAB_CATEGORY[initialMainTab] || 'skincare');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMainTab]);
  
  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  
  // Product requests state
  const [productRequests, setProductRequests] = useState([]);
  const [loadingProductRequests, setLoadingProductRequests] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubcategory, setFilterSubcategory] = useState('all');
  const [filterAvailability, setFilterAvailability] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  // Pagination & Search state
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const PRODUCTS_PER_PAGE = 50;
  
  // Settings state
  const [dataStats, setDataStats] = useState(null);
  const [clearingData, setClearingData] = useState(false);
  const [clearDataDialogOpen, setClearDataDialogOpen] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  // Push notifications hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTestNotification
  } = usePushNotifications();

  // Users management state
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    phone: '',
    password: '',
    role: 'agent',
    employee_code: ''
  });
  const [submittingUser, setSubmittingUser] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    main_ingredient: '',
    description: '',
    image_url: '',
    brand: '',
    price: 0,
    stock_quantity: 0,
    is_available: true,
    indication: '',
    cure_duration_days: null,
    target_concerns: []
  });

  const fetchProducts = useCallback(async (forceLoad = false, searchQuery = '') => {
    // Only load products if forced, or if there's a search query of at least 3 chars
    if (!forceLoad && !searchQuery && !hasSearched) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', PRODUCTS_PER_PAGE.toString());
      params.append('skip', ((currentPage - 1) * PRODUCTS_PER_PAGE).toString());
      
      if (searchQuery && searchQuery.length >= 3) {
        params.append('search', searchQuery);
      }
      
      // Fetch products with pagination
      const response = await axios.get(`${API_URL}/products/all/catalog?${params.toString()}`);
      
      // Handle response - could be array or object with pagination
      if (Array.isArray(response.data)) {
        setProducts(response.data);
        setTotalProducts(response.data.length);
      } else {
        setProducts(response.data.products || response.data);
        setTotalProducts(response.data.total || response.data.length || 0);
      }
      
      setHasSearched(true);
      
      // Also get catalog stats
      try {
        const statsResponse = await axios.get(`${API_URL}/products/all/stats`);
        console.log('Catalog stats:', statsResponse.data);
      } catch (e) {
        console.log('Stats not available');
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de chargement du catalogue' : 'Catalog loading error');
    } finally {
      setLoading(false);
    }
  }, [language, currentPage, hasSearched]);

  useEffect(() => {
    // Don't auto-load products - wait for user search
    // fetchProducts(); // Removed - too many products
    fetchStats();
    fetchReservations();
    fetchProductRequests();
  }, []);

  const fetchReservations = async () => {
    setLoadingReservations(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/pharmacy/reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
    } finally {
      setLoadingReservations(false);
    }
  };

  const fetchProductRequests = async () => {
    setLoadingProductRequests(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/pharmacy/product-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch product requests:', error);
    } finally {
      setLoadingProductRequests(false);
    }
  };

  // ============== USERS MANAGEMENT FUNCTIONS ==============
  const fetchAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement des utilisateurs' : 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [token, language]);

  const handleCreateUser = async () => {
    if (!userFormData.name || !userFormData.phone || !userFormData.password) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }
    setSubmittingUser(true);
    try {
      await axios.post(`${API_URL}/users/create-with-role`, userFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Utilisateur créé' : 'User created');
      setUserDialogOpen(false);
      setUserFormData({ name: '', phone: '', password: '', role: 'agent', employee_code: '' });
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error'));
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Rôle mis à jour' : 'Role updated');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de mise à jour' : 'Update error'));
    }
  };

  const handleUpdateUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit faire au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }
    setSubmittingUser(true);
    try {
      await axios.put(`${API_URL}/users/${editingUser.id}/password`, { new_password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Mot de passe mis à jour' : 'Password updated');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de mise à jour' : 'Update error'));
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setSubmittingUser(true);
    try {
      await axios.delete(`${API_URL}/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de suppression' : 'Delete error'));
    } finally {
      setSubmittingUser(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'supervisor': return 'bg-blue-100 text-blue-800';
      case 'manager': return 'bg-purple-100 text-purple-800';
      case 'agent': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: { fr: 'Admin', en: 'Admin' },
      supervisor: { fr: 'Superviseur', en: 'Supervisor' },
      manager: { fr: 'Manager', en: 'Manager' },
      agent: { fr: 'Agent', en: 'Agent' },
      client: { fr: 'Client', en: 'Client' }
    };
    return labels[role]?.[language === 'fr' ? 'fr' : 'en'] || role;
  };

  const updateProductRequestStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.patch(`${API_URL}/pharmacy/product-requests/${requestId}`, 
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { status: newStatus }
        }
      );
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      fetchProductRequests();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.patch(`${API_URL}/pharmacy/reservations/${reservationId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      fetchReservations();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/products/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchDataStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/admin/data-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataStats(response.data);
    } catch (error) {
      console.error('Failed to fetch data stats:', error);
    }
  }, []);

  const handleClearTestData = async () => {
    setClearingData(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.delete(`${API_URL}/admin/clear-test-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' 
        ? `Données supprimées: ${response.data.deleted.diagnoses} diagnostics, ${response.data.deleted.sales} ventes, ${response.data.deleted.clients} clients`
        : `Deleted: ${response.data.deleted.diagnoses} diagnoses, ${response.data.deleted.sales} sales, ${response.data.deleted.clients} clients`
      );
      fetchDataStats();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Error clearing data');
    } finally {
      setClearingData(false);
      setClearDataDialogOpen(false);
    }
  };

  const handleTogglePushNotifications = async () => {
    const authToken = localStorage.getItem('dermascan_token');
    if (pushSubscribed) {
      const success = await unsubscribePush(authToken);
      if (success) {
        toast.success(language === 'fr' ? 'Notifications désactivées' : 'Notifications disabled');
      }
    } else {
      const success = await subscribePush(authToken);
      if (success) {
        toast.success(language === 'fr' ? 'Notifications activées' : 'Notifications enabled');
      }
    }
  };

  const handleTestNotification = async () => {
    const authToken = localStorage.getItem('dermascan_token');
    const success = await sendTestNotification(authToken);
    if (success) {
      toast.success(language === 'fr' ? 'Notification de test envoyée' : 'Test notification sent');
    }
  };

  const handleSendRenewalNotifications = async () => {
    setSendingNotifications(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(`${API_URL}/push/send-renewal-notifications`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending notifications');
    } finally {
      setSendingNotifications(false);
    }
  };

  const toggleStrategicProduct = async (productId) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.patch(`${API_URL}/products/${productId}/strategic`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, is_strategic: response.data.is_strategic } : p
      ));
      toast.success(response.data.is_strategic 
        ? (language === 'fr' ? 'Produit mis en avant' : 'Product featured')
        : (language === 'fr' ? 'Produit retiré de la mise en avant' : 'Product unfeatured')
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  // Fetch data stats when settings tab is opened
  useEffect(() => {
    if (mainTab === 'settings') {
      fetchDataStats();
    }
    if (mainTab === 'users') {
      fetchAllUsers();
    }
  }, [mainTab, fetchDataStats, fetchAllUsers]);

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      subcategory: '',
      main_ingredient: '',
      description: '',
      image_url: '',
      brand: '',
      price: 0,
      stock_quantity: 0,
      is_available: true,
      indication: '',
      cure_duration_days: null,
      target_concerns: []
    });
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category) {
      toast.error(language === 'fr' ? 'Nom et catégorie requis' : 'Name and category required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        cure_duration_days: formData.cure_duration_days ? parseInt(formData.cure_duration_days) : null
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'fr' ? 'Produit modifié' : 'Product updated');
      } else {
        await axios.post(`${API_URL}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'fr' ? 'Produit créé' : 'Product created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
      main_ingredient: product.main_ingredient || '',
      description: product.description || '',
      image_url: product.image_url || '',
      brand: product.brand || '',
      price: product.price || 0,
      stock_quantity: product.stock_quantity || 0,
      is_available: product.is_available !== false,
      indication: product.indication || '',
      cure_duration_days: product.cure_duration_days || null,
      target_concerns: product.target_concerns || []
    });
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const confirmDelete = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/products/${productToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Produit supprimé' : 'Product deleted');
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setSubmitting(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(`${API_URL}/products/import-excel`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erreurs rencontrées`);
      }
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageFormData = new FormData();
    imageFormData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(`${API_URL}/products/upload-image`, imageFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success(language === 'fr' ? 'Image uploadée' : 'Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = ['name', 'category', 'subcategory', 'brand', 'price', 'stock_quantity', 'description', 'is_available', 'indication', 'cure_duration_days', 'image_url'];
    const example = ['Crème Hydratante', 'cosmetique', 'hydratant', 'BioSkin', '15000', '50', 'Description du produit', 'true', '', '', ''];
    
    const csvContent = [headers.join(','), example.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_produits.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // State for search results from API
  const [apiSearchResults, setApiSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // API search when searchTerm has 3+ characters
  React.useEffect(() => {
    const searchViaApi = async () => {
      if (searchTerm.length >= 3) {
        setIsSearching(true);
        try {
          const response = await axios.get(`${API_URL}/products/all/catalog?search=${encodeURIComponent(searchTerm)}&limit=100`);
          setApiSearchResults(response.data);
        } catch (error) {
          console.error('Search error:', error);
          setApiSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setApiSearchResults([]);
      }
    };
    
    const timer = setTimeout(searchViaApi, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter products - use API results when searching, otherwise local filter
  const filteredProducts = searchTerm.length >= 3 && apiSearchResults.length > 0
    ? apiSearchResults.filter(product => {
        // When searching via API, show all results or filter by active tab
        if (activeTab === 'all') return true;
        if (activeTab === 'autre') return !CATEGORIES[product.category];
        return product.category === activeTab;
      })
    : products.filter(product => {
        const matchesSearch = searchTerm === '' || 
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.code?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
        const matchesSubcategory = filterSubcategory === 'all' || 
          (filterSubcategory === 'none' && !product.subcategory) ||
          product.subcategory === filterSubcategory;
        
        // Match tab - include "autre" for products without known category
        let matchesTab = false;
        if (activeTab === 'all') {
          matchesTab = true;
        } else if (activeTab === 'autre') {
          matchesTab = !CATEGORIES[product.category];
        } else {
          matchesTab = product.category === activeTab;
        }
        
        const matchesAvailability = filterAvailability === 'all' || 
          (filterAvailability === 'available' && product.is_available) ||
          (filterAvailability === 'unavailable' && !product.is_available);
        
        return matchesSearch && matchesCategory && matchesSubcategory && matchesTab && matchesAvailability;
      });

  // Get subcategories for current tab
  const currentSubcategories = activeTab !== 'all' && CATEGORIES[activeTab] 
    ? CATEGORIES[activeTab].subcategories 
    : [];
  
  // Reset subcategory filter when tab changes
  React.useEffect(() => {
    setFilterSubcategory('all');
  }, [activeTab]);

  const getCategoryLabel = (cat) => {
    return CATEGORIES[cat]?.[language === 'fr' ? 'label_fr' : 'label_en'] || cat;
  };

  const getSubcategoryLabel = (cat, subcat) => {
    const sub = CATEGORIES[cat]?.subcategories?.find(s => s.value === subcat);
    return sub?.[language === 'fr' ? 'label_fr' : 'label_en'] || subcat;
  };

  const getCategoryColor = (cat) => {
    return CATEGORIES[cat]?.color || 'bg-gray-100 text-gray-800';
  };

  // Check access rights
  const isAdmin = user?.is_admin || user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isAgent = user?.role === 'agent' || user?.role === 'supervisor';
  // Les agents ont accès à AdminPage UNIQUEMENT pour l'onglet Réception (SADAGAS).
  const isAgentReceptionOnly = isAgent && !isAdmin && !isManager && (initialMainTab === 'reception' || mainTab === 'reception');
  const canAccessAdmin = isAdmin || isManager || isAgentReceptionOnly;

  if (!canAccessAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-[#FFF7ED] to-white">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <p className="text-lg text-[#0F172A]">
              {language === 'fr' 
                ? 'Accès réservé aux administrateurs' 
                : 'Admin access required'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Manager (Cyrille) : autorisé à voir Inventaire, Scoring ET Réception SADAGAS.
  // L'onglet "Imports" historique a été retiré et remplacé par "Saisie manager"
  // qui pointe sur 'scoring'. Le fallback par défaut est donc 'scoring'.
  const managerAllowed = ['inventaire', 'scoring', 'reception'];
  // Agent : autorisé UNIQUEMENT à l'onglet Réception (SADAGAS).
  const agentAllowed = ['reception'];
  const effectiveMainTab = isAgentReceptionOnly
    ? (agentAllowed.includes(mainTab) ? mainTab : 'reception')
    : (isManager && !isAdmin)
      ? (managerAllowed.includes(mainTab) ? mainTab : 'scoring')
      : mainTab;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF7ED] to-white pb-28 sm:pb-32">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E4] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="admin-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#78716C]" />
          </Button>
          <h1 className="text-xl font-semibold text-[#0F172A] flex-1">
            {isAgentReceptionOnly
              ? 'SADAGAS · Réception'
              : isManager && !isAdmin 
                ? 'Espace Manager'
                : (language === 'fr' ? 'Administration' : 'Administration')}
          </h1>
          <Button
            variant="ghost"
            onClick={() => setShowChangePassword(true)}
            className="text-[#78716C] hover:bg-gray-100"
            data-testid="admin-change-password-btn"
            title="Changer mon mot de passe"
          >
            <Lock className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Mot de passe</span>
          </Button>
        </div>
        {/* Main Tabs - Only show for admin */}
        {isAdmin && (
          <div className="max-w-7xl mx-auto px-4 pb-2">
            {/* Niveau 1 : catégories admin (7 sections) */}
            <div className="flex gap-1 mb-2 overflow-x-auto pb-1" data-testid="admin-categories">
              {[
                { id: 'skincare', label: 'Admin SkinCare', color: '#5B3B8C' },
                { id: 'health', label: 'Admin Health', color: '#0E7490' },
                { id: 'rayons', label: 'Admin Rayons', color: '#C9A086' },
                { id: 'gas', label: 'Admin GAS', color: '#0F766E' },
                { id: 'client', label: 'Admin Client', color: '#DB2777' },
                { id: 'rh', label: 'Admin RH', color: '#D97706' },
                { id: 'academy', label: '🎓 SADA Academy', color: '#059669' },
                { id: 'systeme', label: 'Admin Système', color: '#1F2937' },
              ].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setAdminCategory(cat.id)}
                  data-testid={`admin-cat-${cat.id}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition ${
                    adminCategory === cat.id
                      ? 'text-white shadow'
                      : 'text-zinc-600 bg-zinc-100 hover:bg-zinc-200'
                  }`}
                  style={adminCategory === cat.id ? { backgroundColor: cat.color } : {}}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {/* Niveau 2 : tabs filtrés par catégorie */}
            <div className="flex flex-wrap gap-1 border-b border-transparent">
              <button
                onClick={() => setMainTab('products')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'products' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-products"
              style={{ display: adminCategory === 'skincare' ? undefined : 'none' }}
              >
                <Package className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Produits' : 'Products'}
              </button>
              <button
                onClick={() => setMainTab('stats')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'stats'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-stats"
                style={{ display: adminCategory === 'skincare' ? undefined : 'none' }}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Stats' : 'Stats'}
              </button>
              {onNavigateToImport && (
                <button
                  onClick={() => onNavigateToImport()}
                  className="px-4 py-2 rounded-t-lg font-medium transition-colors text-[#C45C26] hover:bg-[#C45C26]/10 border border-[#C45C26]/30"
                  data-testid="main-tab-import-mediciel"
                  title="Importer le fichier d'export Mediciel (mise à jour stock + catégories)"
                style={{ display: adminCategory === 'rayons' ? undefined : 'none' }}
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Import Mediciel
                </button>
              )}
              <button
                onClick={() => setMainTab('reservations')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors relative ${
                  effectiveMainTab === 'reservations' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-reservations"
              style={{ display: adminCategory === 'client' ? undefined : 'none' }}
              >
                <ShoppingCart className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Réservations' : 'Reservations'}
                {reservations.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {reservations.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMainTab('product-requests')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors relative ${
                  effectiveMainTab === 'product-requests' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-product-requests"
              style={{ display: adminCategory === 'client' ? undefined : 'none' }}
              >
                <HelpCircle className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Demandes' : 'Requests'}
                {productRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {productRequests.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMainTab('settings')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'settings' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-settings"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Settings className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Paramètres' : 'Settings'}
              </button>
              <button
                onClick={() => setMainTab('users')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'users' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-users"
              style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
              >
                <Users className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Utilisateurs' : 'Users'}
              </button>
              {isAdmin && (
                <button
                  onClick={() => setMainTab('security')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                    effectiveMainTab === 'security'
                      ? 'bg-[#C9A086] text-white'
                      : 'text-[#78716C] hover:bg-gray-100'
                  }`}
                  data-testid="main-tab-security"
                  style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
                >
                  <Shield className="w-4 h-4 inline mr-2" />
                  {language === 'fr' ? 'Sécurité · Audit' : 'Security · Audit'}
                </button>
              )}
              <button
                onClick={() => setMainTab('performance')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'performance' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-performance"
              style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
              >
                <TrendingUp className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Performance' : 'Performance'}
              </button>
              <button
                onClick={() => setMainTab('sales')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'sales' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-sales"
              style={{ display: adminCategory === 'skincare' ? undefined : 'none' }}
              >
                <ShoppingCart className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Ventes' : 'Sales'}
              </button>
              <button
                onClick={() => setMainTab('data')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'data' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-data"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Database className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Données' : 'Data'}
              </button>
              <button
                onClick={() => setMainTab('blog')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'blog' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-blog"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Blog' : 'Blog'}
              </button>
              <button
                onClick={() => setMainTab('gallery')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'gallery' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-gallery"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Image className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Galerie' : 'Gallery'}
              </button>
              <button
                onClick={() => setMainTab('community')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'community' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-community"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Award className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Communauté' : 'Community'}
              </button>
              <button
                onClick={() => setMainTab('session-config')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'session-config'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-session-config"
                style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Clock className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Session' : 'Session'}
              </button>
              <button
                onClick={() => setMainTab('agent-activity')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'agent-activity' 
                    ? 'bg-[#C9A086] text-white' 
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-agent-activity"
              style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
              >
                <Activity className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Activité Agents' : 'Agent Activity'}
              </button>
              <button
                onClick={() => setMainTab('recrutement')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'recrutement'
                    ? 'bg-[#D97706] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-recrutement"
                style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
                title="Fiches de poste (TDR) publiées sur /recrutement"
              >
                <Briefcase className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Nous rejoindre' : 'Job Postings'}
              </button>
              <button
                onClick={() => setMainTab('marketing')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'marketing'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-marketing"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Megaphone className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Marketing' : 'Marketing'}
              </button>
              <button
                onClick={() => setMainTab('sms')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'sms'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-sms"
              style={{ display: adminCategory === 'client' ? undefined : 'none' }}
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                SMS
              </button>
              <button
                onClick={() => setMainTab('b2b')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'b2b'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-b2b"
                title="SADA B2B · Suivi des dossiers cliniques"
                style={{ display: adminCategory === 'client' ? undefined : 'none' }}
              >
                <Building2 className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'SADA B2B' : 'SADA B2B'}
              </button>
              <button
                onClick={() => setMainTab('mediciel-auto')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'mediciel-auto'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-mediciel-auto"
                title="Auto-import quotidien Mediciel par email"
              style={{ display: adminCategory === 'rayons' ? undefined : 'none' }}
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Auto Mediciel
              </button>
              <button
                onClick={() => setMainTab('mediciel-realtime')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'mediciel-realtime'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-mediciel-realtime"
                title="Stock Mediciel temps réel via API"
              style={{ display: adminCategory === 'rayons' ? undefined : 'none' }}
              >
                <RefreshCw className="w-4 h-4 inline mr-2" />
                Stock Temps Réel
              </button>
              <button
                onClick={() => setMainTab('inventaire')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'inventaire'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-inventaire"
                title="Module inventaire tournant — maquette"
              style={{ display: adminCategory === 'rayons' ? undefined : 'none' }}
              >
                <ClipboardList className="w-4 h-4 inline mr-2" />
                Inventaire
              </button>
              <button
                onClick={() => setMainTab('reception')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'reception'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-reception"
                title="Réception produits avec IA"
              style={{ display: adminCategory === 'gas' ? undefined : 'none' }}
              >
                <Truck className="w-4 h-4 inline mr-2" />
                Réception
              </button>
              <button
                onClick={() => setMainTab('agent-performance')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'agent-performance'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-agent-performance"
                title="Pilotage performance agents — Héros du mois"
              style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
              >
                <Award className="w-4 h-4 inline mr-2" />
                Performance Agents
              </button>
              <button
                onClick={() => setMainTab('scoring')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'scoring'
                    ? 'bg-[#D97706] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-scoring"
                title="Scoring & barèmes paramétrables (par défaut + par agent + Cyrille)"
                style={{ display: adminCategory === 'rh' ? undefined : 'none' }}
              >
                <Award className="w-4 h-4 inline mr-2" />
                Scoring & Barèmes
              </button>
              <button
                onClick={() => setMainTab('notifications')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'notifications'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-notifications"
                title="Préférences d'envoi des emails automatiques"
                style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Bell className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Notifications' : 'Notifications'}
              </button>
              <button
                onClick={() => setMainTab('appearance')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'appearance'
                    ? 'bg-[#C9A086] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-appearance"
              style={{ display: adminCategory === 'systeme' ? undefined : 'none' }}
              >
                <Palette className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Apparence' : 'Appearance'}
              </button>
              <button
                onClick={() => setMainTab('academy')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'academy'
                    ? 'bg-[#059669] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="main-tab-academy"
                title="SADA Academy — formations & certifications"
                style={{ display: adminCategory === 'academy' ? undefined : 'none' }}
              >
                <GraduationCap className="w-4 h-4 inline mr-2" />
                SADA Academy
              </button>
            </div>
          </div>
        )}

        {/* Mini-nav pour les managers — Saisie manager + Inventaire uniquement */}
        {isManager && !isAdmin && (
          <div className="max-w-7xl mx-auto px-4 pb-2">
            <div className="flex flex-wrap gap-1 border-b border-transparent">
              <button
                onClick={() => setMainTab('scoring')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'scoring'
                    ? 'bg-[#D97706] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="manager-tab-saisie"
                title="Saisie des notes manager (Appréciation manager direct)"
              >
                <ClipboardList className="w-4 h-4 inline mr-2" />
                Saisie manager
              </button>
              <button
                onClick={() => setMainTab('inventaire')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'inventaire'
                    ? 'bg-[#5B3B8C] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="manager-tab-inventaire"
              >
                <ClipboardList className="w-4 h-4 inline mr-2" />
                SADAStock · Inventaire
              </button>
              <button
                onClick={() => setMainTab('reception')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  effectiveMainTab === 'reception'
                    ? 'bg-[#0F766E] text-white'
                    : 'text-[#78716C] hover:bg-gray-100'
                }`}
                data-testid="manager-tab-reception"
                title="Réception SADAGAS — bons de livraison &amp; entrées de stock"
              >
                <Truck className="w-4 h-4 inline mr-2" />
                SADAGAS · Réception
              </button>
              {/* Tab "Scoring & Barèmes" retiré du nav manager — Cyrille y accède
                  via "Saisie manager" ci-dessus, qui pointe sur la même route.
                  Évite la confusion d'avoir 2 entrées pour la même destination. */}
            </div>
          </div>
        )}

        {/* Mini-nav pour les agents (uniquement Réception SADAGAS) */}
        {isAgentReceptionOnly && (
          <div className="max-w-7xl mx-auto px-4 pb-2">
            <div className="flex flex-wrap gap-1 border-b border-transparent">
              <button
                onClick={() => setMainTab('reception')}
                className="px-4 py-2 rounded-t-lg font-medium transition-colors bg-[#0F766E] text-white"
                data-testid="agent-tab-reception"
              >
                <Truck className="w-4 h-4 inline mr-2" />
                SADAGAS · Réception
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        
        {/* === RESERVATIONS TAB === */}
        {effectiveMainTab === 'reservations' && (
          <ReservationsTab />
        )}

        {/* === PRODUCT REQUESTS TAB === */}
        {effectiveMainTab === 'product-requests' && (
          <ProductRequestsTab />
        )}
        
        {/* === SETTINGS TAB === */}
        {effectiveMainTab === 'settings' && (
          <SettingsTab 
            onNavigateToImport={() => setMainTab('products')}
            isAdmin={isAdmin}
            isManager={isManager}
          />
        )}

        {/* === USERS TAB === */}
        {effectiveMainTab === 'users' && (
          <UsersManagement />
        )}

        {/* === SECURITY / AUDIT TAB === */}
        {effectiveMainTab === 'security' && isAdmin && (
          <AuditLogPanel />
        )}

        {/* === PERFORMANCE TAB === */}
        {effectiveMainTab === 'performance' && (
          <PerformanceDashboard />
        )}

        {/* === STATS TAB (SkinCare) === */}
        {effectiveMainTab === 'stats' && (
          <StatsPage onBack={() => setMainTab('products')} />
        )}

        {/* === SALES MANAGEMENT TAB === */}
        {effectiveMainTab === 'sales' && (
          <SalesManagement language={language} />
        )}

        {/* === DATA MANAGEMENT TAB === */}
        {effectiveMainTab === 'data' && (
          <DataManagement language={language} />
        )}

        {/* === BLOG MANAGEMENT TAB === */}
        {effectiveMainTab === 'blog' && (
          <BlogManagement language={language} />
        )}

        {/* === GALLERY MANAGEMENT TAB === */}
        {effectiveMainTab === 'gallery' && (
          <GalleryManagement language={language} />
        )}

        {/* === COMMUNITY MANAGEMENT TAB === */}
        {effectiveMainTab === 'community' && (
          <CommunityManagement language={language} />
        )}

        {/* === AGENT ACTIVITY TAB === */}
        {effectiveMainTab === 'agent-activity' && (
          <AgentActivityDashboard language={language} />
        )}

        {/* === MARKETING TAB === */}
        {effectiveMainTab === 'marketing' && (
          <MarketingTab />
        )}

        {/* === SMS TAB === */}
        {effectiveMainTab === 'sms' && (
          <SMSManagement />
        )}

        {/* === MEDICIEL AUTO IMPORT TAB === */}
        {effectiveMainTab === 'mediciel-auto' && (
          <MedicielAutoTab />
        )}

        {/* === MEDICIEL REALTIME API === */}
        {effectiveMainTab === 'mediciel-realtime' && (
          <MedicielRealtimeTab />
        )}

        {/* === INVENTAIRE TOURNANT === */}
        {effectiveMainTab === 'inventaire' && (
          <InventoryTab />
        )}

        {/* === RÉCEPTION PRODUITS === */}
        {effectiveMainTab === 'reception' && (
          <ReceptionTab />
        )}

        {/* === PERFORMANCE AGENTS === */}
        {effectiveMainTab === 'agent-performance' && (
          <AgentPerformanceTab />
        )}

        {/* === SCORING & BARÈMES === */}
        {effectiveMainTab === 'scoring' && (
          <ScoringTab />
        )}

        {/* === APPEARANCE TAB === */}
        {effectiveMainTab === 'appearance' && (
          <AppearanceTab />
        )}

        {/* === SESSION CONFIG TAB === */}
        {effectiveMainTab === 'session-config' && (
          <SessionConfigTab />
        )}

        {/* === NOTIFICATION PREFERENCES TAB === */}
        {effectiveMainTab === 'notifications' && (
          <NotificationPreferencesTab />
        )}

        {/* === SADA ACADEMY TAB === */}
        {effectiveMainTab === 'academy' && (
          <AcademyTab />
        )}

        {/* === RECRUTEMENT (TDR) TAB === */}
        {effectiveMainTab === 'recrutement' && (
          <JobPostingsTab />
        )}

        {/* === B2B CLINIQUES TAB === */}
        {effectiveMainTab === 'b2b' && (
          <B2BTab />
        )}

        {/* === PRODUCTS TAB === */}
        {effectiveMainTab === 'products' && (
          <>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Package className="w-8 h-8 mx-auto mb-2 text-[#0EA5E9]" />
                <p className="text-2xl font-bold text-[#0F172A]">{stats.total_products}</p>
                <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Produits' : 'Products'}</p>
              </CardContent>
            </Card>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <Card key={key}>
                <CardContent className="p-4 text-center">
                  <div className={`w-8 h-8 mx-auto mb-2 rounded-full ${cat.color} flex items-center justify-center`}>
                    <span className="text-sm font-bold">{stats.by_category?.[key]?.count || 0}</span>
                  </div>
                  <p className="text-sm font-medium text-[#0F172A]">
                    {language === 'fr' ? cat.label_fr : cat.label_en}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Actions Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-2">
                <Button 
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="bg-[#0EA5E9] hover:bg-[#0284C7]"
                  data-testid="add-product-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Nouveau produit' : 'New product'}
                </Button>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {language === 'fr' ? 'Importer' : 'Import'}
                </Button>

                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Modèle CSV' : 'CSV Template'}
                </Button>
              </div>

              {/* Search */}
              <div className="flex gap-2 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#78716C]" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher (3 car. min)...' : 'Search (3 char min)...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-products"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-[#C9A086]" />
                  )}
                </div>
                
                {searchTerm.length >= 3 && apiSearchResults.length > 0 && (
                  <Badge className="bg-green-100 text-green-700">
                    {apiSearchResults.length} résultat{apiSearchResults.length > 1 ? 's' : ''} trouvé{apiSearchResults.length > 1 ? 's' : ''}
                  </Badge>
                )}
                
                <Select value={filterAvailability} onValueChange={setFilterAvailability}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    <SelectItem value="available">{language === 'fr' ? 'Disponibles' : 'Available'}</SelectItem>
                    <SelectItem value="unavailable">{language === 'fr' ? 'Indisponibles' : 'Unavailable'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products by Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start overflow-x-auto flex-wrap">
            <TabsTrigger value="all" data-testid="tab-all">
              {language === 'fr' ? 'Tous' : 'All'} ({products.length})
            </TabsTrigger>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <TabsTrigger key={key} value={key} data-testid={`tab-${key}`}>
                {language === 'fr' ? cat.label_fr : cat.label_en} 
                ({products.filter(p => p.category === key).length})
              </TabsTrigger>
            ))}
            <TabsTrigger value="autre" data-testid="tab-autre">
              {language === 'fr' ? 'Autres' : 'Other'} 
              ({products.filter(p => !CATEGORIES[p.category]).length})
            </TabsTrigger>
          </TabsList>

          {/* Subcategory Filter - shown when a category tab is selected */}
          {currentSubcategories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setFilterSubcategory('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterSubcategory === 'all'
                    ? 'bg-[#C9A086] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {language === 'fr' ? 'Tous' : 'All'}
              </button>
              {currentSubcategories.map(sub => {
                const count = products.filter(p => p.category === activeTab && p.subcategory === sub.value).length;
                return (
                  <button
                    key={sub.value}
                    onClick={() => setFilterSubcategory(sub.value)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filterSubcategory === sub.value
                        ? 'bg-[#C9A086] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {language === 'fr' ? sub.label_fr : sub.label_en} ({count})
                  </button>
                );
              })}
              <button
                onClick={() => setFilterSubcategory('none')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterSubcategory === 'none'
                    ? 'bg-[#C9A086] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {language === 'fr' ? 'Non classé' : 'Uncategorized'} ({products.filter(p => p.category === activeTab && !p.subcategory).length})
              </button>
            </div>
          )}

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
              </div>
            ) : !hasSearched && searchTerm.length < 3 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Search className="w-16 h-16 mx-auto mb-4 text-[#C9A086] opacity-70" />
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                    {language === 'fr' ? 'Recherchez un produit' : 'Search for a product'}
                  </h3>
                  <p className="text-[#78716C] mb-4">
                    {language === 'fr' 
                      ? 'Entrez au moins 3 caractères dans la barre de recherche pour afficher les produits.' 
                      : 'Enter at least 3 characters in the search bar to display products.'}
                  </p>
                  <p className="text-sm text-[#78716C]">
                    {language === 'fr' 
                      ? `Catalogue: ${stats?.total_products || 0} produits disponibles` 
                      : `Catalog: ${stats?.total_products || 0} products available`}
                  </p>
                  <div className="mt-6">
                    <Button 
                      variant="outline" 
                      onClick={() => fetchProducts(true)}
                      className="border-[#C9A086] text-[#C9A086] hover:bg-[#C9A086] hover:text-white"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Charger tous les produits (lent)' : 'Load all products (slow)'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : filteredProducts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 mx-auto mb-4 text-[#78716C] opacity-50" />
                  <p className="text-[#78716C]">
                    {language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Pagination info */}
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-[#78716C]">
                    {language === 'fr' 
                      ? `${filteredProducts.length} produit(s) affiché(s)` 
                      : `${filteredProducts.length} product(s) displayed`}
                  </p>
                  {isSearching && (
                    <div className="flex items-center gap-2 text-sm text-[#0EA5E9]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'fr' ? 'Recherche...' : 'Searching...'}
                    </div>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredProducts.map((product) => (
                  <Card 
                    key={product.id} 
                    className={`relative ${!product.is_available ? 'opacity-60' : ''}`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Image */}
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {product.image_url ? (
                            <img 
                              src={product.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : product.image_url}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Package className="w-8 h-8 text-[#78716C]" />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-medium text-[#0F172A] truncate">{product.name}</h3>
                              {product.brand && (
                                <p className="text-sm text-[#78716C]">{product.brand}</p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className={`h-8 w-8 ${product.is_strategic ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'}`}
                                onClick={() => toggleStrategicProduct(product.id)}
                                title={product.is_strategic ? 'Retirer de la mise en avant' : 'Mettre en avant'}
                              >
                                <Star className={`w-4 h-4 ${product.is_strategic ? 'fill-yellow-500' : ''}`} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handleEdit(product)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-red-500 hover:text-red-700"
                                onClick={() => confirmDelete(product)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-1 mt-2">
                            <Badge className={getCategoryColor(product.category)}>
                              {getCategoryLabel(product.category)}
                            </Badge>
                            {product.subcategory && (
                              <Badge variant="outline" className="text-xs">
                                {getSubcategoryLabel(product.category, product.subcategory)}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <p className="font-semibold text-[#0EA5E9]">
                              {product.price?.toLocaleString()} FCFA
                            </p>
                            <div className="flex items-center gap-2">
                              {product.is_available ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Stock: {product.stock_quantity || 0}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Indisponible' : 'Unavailable'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              </>
            )}
          </TabsContent>
        </Tabs>
          </>
        )}
      </div>

      {/* Product Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct 
                ? (language === 'fr' ? 'Modifier le produit' : 'Edit product')
                : (language === 'fr' ? 'Nouveau produit' : 'New product')
              }
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Name */}
              <div className="col-span-2">
                <Label>{language === 'fr' ? 'Nom du produit' : 'Product name'} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Crème hydratante BioSkin"
                  required
                  data-testid="product-name-input"
                />
              </div>

              {/* Category */}
              <div>
                <Label>{language === 'fr' ? 'Catégorie' : 'Category'} *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({ ...formData, category: val, subcategory: '' })}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>
                        {language === 'fr' ? cat.label_fr : cat.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategory */}
              <div>
                <Label>{language === 'fr' ? 'Sous-catégorie' : 'Subcategory'}</Label>
                <Select 
                  value={formData.subcategory} 
                  onValueChange={(val) => setFormData({ ...formData, subcategory: val })}
                  disabled={!formData.category}
                >
                  <SelectTrigger data-testid="product-subcategory-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.category && CATEGORIES[formData.category]?.subcategories.map((sub) => (
                      <SelectItem key={sub.value} value={sub.value}>
                        {language === 'fr' ? sub.label_fr : sub.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Brand */}
              <div>
                <Label>{language === 'fr' ? 'Marque' : 'Brand'}</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="Ex: BioSkin, L'Oréal..."
                />
              </div>

              {/* Price */}
              <div>
                <Label>{language === 'fr' ? 'Prix (FCFA)' : 'Price (FCFA)'}</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  min="0"
                  data-testid="product-price-input"
                />
              </div>

              {/* Stock */}
              <div>
                <Label>{language === 'fr' ? 'Quantité en stock' : 'Stock quantity'}</Label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  min="0"
                />
              </div>

              {/* Availability */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_available}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                />
                <Label>{language === 'fr' ? 'Disponible à la vente' : 'Available for sale'}</Label>
              </div>

              {/* Description */}
              <div className="col-span-2">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={language === 'fr' ? 'Description du produit...' : 'Product description...'}
                  rows={3}
                />
              </div>

              {/* Indication (for supplements) */}
              {(formData.category === 'complement' || formData.category === 'spa') && (
                <>
                  <div className="col-span-2">
                    <Label>{language === 'fr' ? 'Indication / Usage' : 'Indication / Usage'}</Label>
                    <Input
                      value={formData.indication}
                      onChange={(e) => setFormData({ ...formData, indication: e.target.value })}
                      placeholder={language === 'fr' ? 'Ex: Fatigue, stress, peau sèche...' : 'Ex: Fatigue, stress, dry skin...'}
                    />
                  </div>
                  <div>
                    <Label>{language === 'fr' ? 'Durée de cure (jours)' : 'Cure duration (days)'}</Label>
                    <Input
                      type="number"
                      value={formData.cure_duration_days || ''}
                      onChange={(e) => setFormData({ ...formData, cure_duration_days: e.target.value })}
                      min="0"
                      placeholder="30, 60, 90..."
                    />
                  </div>
                </>
              )}

              {/* Image */}
              <div className="col-span-2">
                <Label>{language === 'fr' ? 'Image du produit' : 'Product image'}</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder={language === 'fr' ? 'URL de l\'image ou uploader' : 'Image URL or upload'}
                    className="flex-1"
                  />
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Image className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {formData.image_url && (
                  <div className="mt-2 w-24 h-24 rounded-lg overflow-hidden border">
                    <img 
                      src={formData.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#0EA5E9]">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingProduct 
                  ? (language === 'fr' ? 'Enregistrer' : 'Save')
                  : (language === 'fr' ? 'Créer' : 'Create')
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Confirmer la suppression' : 'Confirm deletion'}</DialogTitle>
          </DialogHeader>
          <p className="text-[#78716C]">
            {language === 'fr' 
              ? `Voulez-vous vraiment supprimer "${productToDelete?.name}" ?`
              : `Are you sure you want to delete "${productToDelete?.name}"?`
            }
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Test Data Confirmation Dialog */}
      <Dialog open={clearDataDialogOpen} onOpenChange={setClearDataDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              {language === 'fr' ? 'Confirmer la réinitialisation' : 'Confirm reset'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#78716C]">
              {language === 'fr' 
                ? 'Cette action va supprimer définitivement :'
                : 'This action will permanently delete:'}
            </p>
            <ul className="list-disc list-inside text-red-600 space-y-1">
              <li>{language === 'fr' ? 'Tous les diagnostics' : 'All diagnoses'}</li>
              <li>{language === 'fr' ? 'Toutes les ventes' : 'All sales'}</li>
              <li>{language === 'fr' ? 'Tous les clients' : 'All clients'}</li>
            </ul>
            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-lg">
              {language === 'fr' 
                ? 'Les utilisateurs, produits et compléments seront conservés.'
                : 'Users, products and supplements will be preserved.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDataDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearTestData}
              disabled={clearingData}
              data-testid="confirm-clear-data-btn"
            >
              {clearingData && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#0EA5E9]" />
              {language === 'fr' ? 'Créer un utilisateur' : 'Create User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom complet *' : 'Full Name *'}</Label>
              <Input
                value={userFormData.name}
                onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                placeholder="Ex: Jean Dupont"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone *' : 'Phone *'}</Label>
              <Input
                type="tel"
                value={userFormData.phone}
                onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                placeholder="Ex: 0770000000"
                data-testid="user-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Mot de passe *' : 'Password *'}</Label>
              <Input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                placeholder="Min. 6 caractères"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
              <Select
                value={userFormData.role}
                onValueChange={(val) => setUserFormData({...userFormData, role: val})}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">{language === 'fr' ? 'Agent' : 'Agent'}</SelectItem>
                  <SelectItem value="supervisor">{language === 'fr' ? 'Superviseur' : 'Supervisor'}</SelectItem>
                  <SelectItem value="manager">{language === 'fr' ? 'Manager' : 'Manager'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Code employé (optionnel)' : 'Employee Code (optional)'}</Label>
              <Input
                value={userFormData.employee_code}
                onChange={(e) => setUserFormData({...userFormData, employee_code: e.target.value})}
                placeholder="Ex: EMP-001"
                data-testid="user-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={submittingUser}
              className="bg-[#0EA5E9]"
              data-testid="create-user-submit"
            >
              {submittingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Créer' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#C9A086]" />
              {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingUser && (
              <p className="text-sm text-[#78716C]">
                {language === 'fr' ? 'Utilisateur:' : 'User:'} <strong>{editingUser.name}</strong>
              </p>
            )}
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nouveau mot de passe *' : 'New Password *'}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 caractères"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setPasswordDialogOpen(false);
              setNewPassword('');
              setEditingUser(null);
            }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleUpdateUserPassword} 
              disabled={submittingUser}
              className="bg-[#C9A086]"
              data-testid="update-password-submit"
            >
              {submittingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Mettre à jour' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {language === 'fr' ? 'Supprimer l\'utilisateur' : 'Delete User'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {userToDelete && (
              <p>
                {language === 'fr' 
                  ? `Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete.name}" ?`
                  : `Are you sure you want to delete user "${userToDelete.name}"?`}
              </p>
            )}
            <p className="text-sm text-red-500 mt-2">
              {language === 'fr' 
                ? 'Cette action est irréversible.'
                : 'This action cannot be undone.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteUserDialogOpen(false);
              setUserToDelete(null);
            }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteUser} 
              disabled={submittingUser}
              data-testid="confirm-delete-user"
            >
              {submittingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showChangePassword && (
        <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default AdminPage;
